import { pluralizeTemplate } from "#ce/apps/SpAdmin/shared/utils/pluralize";

// Estimates at or above this many seconds are shown in whole minutes, so the
// countdown does not tick by the second.
const MINUTES_THRESHOLD_SECONDS = 90;

/**
 * Returns the whole-number completion percentage of a `{ processed, total }`
 * progress report, capped at 100; `null` without a report or before the total is
 * known. `{ processed: 400, total: 8000 }` → `5`.
 *
 * @param {{ processed: number, total: number } | null} progress
 * @returns {number | null}
 */
export function progressPercent(progress) {
  if (!progress || progress.total <= 0) {
    return null;
  }

  return Math.min(100, Math.round((progress.processed / progress.total) * 100));
}

/**
 * Returns the row counter sentence from the `rows` template, e.g.
 * `"400 of 8000 rows previewed"`; an empty string without a report or before the
 * total is known.
 *
 * @param {{ processed: number, total: number } | null} progress
 * @param {{ rows: string }} labels - The `progress` label group.
 * @returns {string}
 */
export function formatProgressText(progress, labels) {
  if (!progress || progress.total <= 0) {
    return "";
  }

  return labels.rows.replaceAll("%{processed}", progress.processed).replaceAll("%{total}", progress.total);
}

/**
 * Returns the remaining-time sentence from the `remaining` template, e.g.
 * `"About 4 minutes remaining"`; an empty string when no positive estimate is
 * known. Below `MINUTES_THRESHOLD_SECONDS` the estimate is given in seconds.
 *
 * @param {number | null | undefined} seconds
 * @param {{ remaining: string, minutes: Object, seconds: Object }} labels - The `progress` label group.
 * @returns {string}
 */
export function formatRemainingText(seconds, labels) {
  if (seconds === null || seconds === undefined || seconds <= 0) {
    return "";
  }

  let time;
  if (seconds >= MINUTES_THRESHOLD_SECONDS) {
    time = pluralizeTemplate(labels.minutes, Math.round(seconds / 60));
  } else {
    time = pluralizeTemplate(labels.seconds, Math.round(seconds));
  }

  return labels.remaining.replaceAll("%{time}", time);
}
